import React, { useContext, useEffect, useState } from "react";
import { useNotification } from "../../NotificationContext";
import { getEpisodeType, saveEpisodeType, updateEpisodeType } from "../../apis/episodeType";
import { LanguageContext } from "../../providers/LanguageProvider";

const AddEpisodeType: React.FC<{ closeModal: () => void; editableId: any }> = ({
  closeModal,
  editableId,
}) => {
  const defaultEpisodeType = {
    name: "",
    description: "",
    meta: [],
  };
  const [episodeType, setEpisodeType] = useState(defaultEpisodeType);
  const [metaFieldsJson, setMetaFieldsJson] = useState("[]");
  const [errors, setErrors] = useState<any>();
  const { showNotification } = useNotification();
  const { translate: t } = useContext(LanguageContext);

  useEffect(() => {
    if (editableId) {
      getEpisodeType(editableId).then((res: any) => {
        const data = res.data?.data || res.data;
        setEpisodeType({
          name: data?.name || "",
          description: data?.description || "",
          meta: data?.metaFields || data?.meta || [],
        });
        setMetaFieldsJson(
          JSON.stringify(data?.metaFields || data?.meta || [], null, 2)
        );
      });
    }
  }, [editableId]);

  const validateForm = () => {
    const validationErrors: any = {};
    if (!episodeType.name.trim()) {
      validationErrors.name = "* Name is required";
    }
    let parsedMeta: any = [];
    try {
      parsedMeta = JSON.parse(metaFieldsJson || "[]");
      if (!Array.isArray(parsedMeta) && typeof parsedMeta !== "object") {
        validationErrors.meta = "* Meta fields should be an array or object";
      }
    } catch {
      validationErrors.meta = "* Invalid JSON format for meta fields";
    }
    return { validationErrors, parsedMeta };
  };

  const handleSave = () => {
    const { validationErrors, parsedMeta } = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const payload = {
      ...episodeType,
      meta: parsedMeta,
    };
    if (editableId) {
      updateEpisodeType({ id: editableId, ...payload }).then(() => {
        showNotification("success", "Episode type updated", "", "topRight");
        closeModal();
      });
    } else {
      saveEpisodeType(payload).then(() => {
        showNotification("success", "Episode type created", "", "topRight");
        closeModal();
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          {t("name")}
        </label>
        <div className="mt-1">
          <input
            type="text"
            value={episodeType.name}
            onChange={(e) => setEpisodeType({ ...episodeType, name: e.target.value })}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        {errors?.name && (
          <span className="text-sm text-red-500 leading-0">{errors.name}</span>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Description
        </label>
        <div className="mt-1">
          <textarea
            rows={3}
            value={episodeType.description}
            onChange={(e) =>
              setEpisodeType({ ...episodeType, description: e.target.value })
            }
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Meta Fields (JSON)
        </label>
        <div className="mt-1">
          <textarea
            rows={6}
            value={metaFieldsJson}
            onChange={(e) => setMetaFieldsJson(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 font-mono"
          />
        </div>
        {errors?.meta && (
          <span className="text-sm text-red-500 leading-0">{errors.meta}</span>
        )}
      </div>

      <div className="flex items-center justify-start gap-x-6 border-gray-900/10 ">
        <button
          onClick={handleSave}
          type="button"
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dark"
        >
          {editableId ? t("update") : t("save")}
        </button>
        <button
          onClick={closeModal}
          type="button"
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
};

export default AddEpisodeType;
